/* eslint-disable import/exports-last */
import { HttpErrorResponse } from '@angular/common/http';

import { FormValueFlatten } from '../../../services/form-helper.service';
import { CustomContext, ErrorType, GA4EventDataParam } from '../models/configuration.model';
import { Ga4SelectItemActionType } from '../models/ecommerce.model';
import {
    Ga4CFValues,
    GA4EventDataGroup,
    GA4EventDataValues,
    Ga4EventLocation,
    GA4ImActionType,
    Ga4ImType,
    Ga4ImTypeForLead,
    Ga4ImTypeForSearch,
    GA4SearchInfo,
} from '../models/event-data.model';
import { GA4EventType } from '../models/general.model';
import * as Ga4DataUtils from '../utils/ga4-data.utils';

import { getFullPageTitle, isValidModal } from './ga4-context.helper';
import * as GA4EcommerceHelpers from './ga4-ecommerce.helper';

import { CartItem } from '@api/cart/cart.models';
import { Category } from '@core/domain/models/category.models';
import { PageSection } from '@core/domain/types/page-sections.types';
import { ProductPublicModels } from '@features/products/product';
import { FilterEmitter, orderTypes } from '@pages/category';
import { SearchOrigins } from '@pages/search/domain/state/search.state';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { MenuCategory } from '@shared/header/models/category.model';
import { gridTypeToNumberOfProducts } from '@shared/models/grid-type.model';
import { RelatedProductType } from '@shared/product/related-products/domain/models/related-products.models';
import { ModalData } from '@ui/dialog/store/modal.state';

export const defaultConfig: GA4EventDataGroup = {
    content_id: undefined,
    content_type: undefined,
    content_value: undefined,
    form_name: undefined,
    im_action_type: undefined,
    im_app_store: undefined,
    im_bundle_reference: undefined,
    im_carousel_index: undefined,
    im_category_cross_type: undefined,
    im_category_view_type: undefined,
    im_collection_reference: undefined,
    im_detail: undefined,
    im_event_data_cf: undefined,
    im_event_name: undefined,
    im_item_id: undefined,
    im_items_number: undefined,
    im_link_category: undefined,
    im_link_page_title: undefined,
    im_link_type: undefined,
    im_location: undefined,
    im_order_state: undefined,
    im_photo_reference: undefined,
    im_photo_type: undefined,
    im_physical_store_id: undefined,
    im_product_color: undefined,
    im_product_size: undefined,
    im_shipping_tier: undefined,
    im_store_zip_code: undefined,
    im_transaction_id: undefined,
    im_type: undefined,
    item_id: undefined,
    link_text: undefined,
    link_url: undefined,
    method: undefined,
    search_term: undefined,
    size_recommender_name: undefined,
};

export const getBundleReference = (item: CartItem | ProductPublicModels.PublicProduct): string | undefined => {
    if (Ga4DataUtils.isProduct(item)) {
        const product: ProductPublicModels.PublicProduct = { ...item } as ProductPublicModels.PublicProduct;
        return product.reference;
    }
    return undefined;
};

export const getIdFromMediaPath = (mediaPath: string): string => {
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    const mediaFileName = /\w+\.\w+\?/.exec(mediaPath);
    return mediaFileName[0].split('.')[0];
};

export const getPhotoReference = (item: ProductPublicModels.PublicProduct, mediaPath: string): string | undefined => {
    if (!!item && item?.imageData?.ref) {
        return item?.imageData?.ref;
    }
    if (mediaPath) {
        return getIdFromMediaPath(mediaPath);
    }
    return undefined;
};

export const getItemsNumber = (values: GA4EventDataValues): string | undefined => {
    switch (values.eventType) {
        case GA4EventType.search: {
            // biome-ignore lint/suspicious/useIsArray: <explanation>
            if (values.items instanceof Array) {
                return values.items.length.toString();
            }
            return values.items?.products?.length.toString();
        }
        default:
            return (values?.customCategoryProductsIdsLength
                || values?.categoryProductsIdsLength)?.toString();
    }
};

export const getImTypeForModal = (modalData: ModalData<any>): Ga4ImTypeForLead | string | undefined => {
    switch (modalData.name) {
        case 'app-feel-subscription-modal':
            return 'fidelizacion';
        default:
            return undefined;
    }
};
export const getImTypeForSearch = (origin: SearchOrigins): Ga4ImTypeForSearch | undefined => {
    switch (origin) {
        case 'default':
            return 'manual';

        case 'empathize_trending':
            return 'trending';

        case 'history':
            return 'ultimas_busquedas';

        case 'linked':
        case 'empathize_category':
            return 'busqueda_relacionada';

        default:
            return undefined;
    }
};

export const getImTypeByEventFrom = (values: GA4EventDataValues): Ga4ImType | undefined => {
    switch (values.eventFrom) {
        case EventFrom.search:
        case EventFrom.searchNoResults:
            return getImTypeForSearch(values.searchInfo.origin);

        case EventFrom.searchAdapter:
        case EventFrom.searchAdapterNoResults:
            return values.searchInfo.searchType;

        case EventFrom.shopcart:
            return 'wishlist';

        case EventFrom.feelJoin:
            return 'fidelizacion';

        case EventFrom.newsletter: {
            const newsletterString = values.newsletterForm?.sections?.map(
                (section) => section.replace('NEWSLETTER_', '')
            ).join('|') || '';
            return `newsletter_${newsletterString.toLowerCase()}` as Ga4ImType;
        }

        case EventFrom.filter:
            return values.imType;

        default:
            return undefined;
    }
};

export const getImType = (values: GA4EventDataValues): string | undefined => {
    if (isValidModal(values.modal)) {
        return getImTypeForModal(values.modal);
    }
    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!values.errorType) {
        return values.errorType;
    }
    return getImTypeByEventFrom(values);
};

export const getCollectionReference = (category: Category | MenuCategory): string => {
    return category?.nameEn;
};

export const getImEventDataCFForModals = (modalData: ModalData<any>): Ga4CFValues | undefined => {
    switch (modalData.name) {
        case 'app-feel-subscription-modal':
            return 'corporativo';
        default:
            return undefined;
    }
};

export const getImEventDataCFByEventFrom = (eventFrom: EventFrom, checkoutAuth?: boolean): Ga4CFValues | undefined => {
    switch (eventFrom) {
        case EventFrom.search:
        case EventFrom.searchNoResults:
        case EventFrom.searchAdapter:
        case EventFrom.searchAdapterNoResults:
        case EventFrom.filter:
            return 'catalogo';

        case EventFrom.feelJoin:
            return 'perfil_usuario';

        case EventFrom.newsletter:
            return 'newsletter';

        case EventFrom.signUp:
        case EventFrom.login:
            return checkoutAuth ? 'perfil_comprador' : 'perfil_usuario';

        case EventFrom.parrilla:
            return 'perfil_comprador';

        case EventFrom.header:
        case EventFrom.mainMenu:
            return 'buscador';

        case EventFrom.authentication:
            return 'acceso';

        case EventFrom.checkout:
            return 'checkout';

        case EventFrom.error:
            return 'error';

        case EventFrom.chat:
            return 'ayuda';

        default:
            return undefined;
    }
};

export const getImEventDataCF = (
    eventFrom: EventFrom,
    modalData?: ModalData<any>,
    checkoutAuth?: boolean,
): Ga4CFValues | undefined => {
    if (isValidModal(modalData)) {
        return getImEventDataCFForModals(modalData);
    }
    return getImEventDataCFByEventFrom(eventFrom, checkoutAuth);
};

export const getImLocationByPage = (page?: PageSection): Ga4EventLocation => {
    switch (page) {
        case 'ItxAngularMyAccountPage':
        case 'ItxMyAccountMenuPage':
            return Ga4EventLocation.miCuenta;

        case 'ItxAngularShopcartPage':
            return Ga4EventLocation.cesta;

        case 'ItxCategoryPage':
            return Ga4EventLocation.parrilla;

        case 'ItxHomeBgPage':
        case 'ItxHomeMenPage':
        case 'ItxHomePage':
        case 'ItxHomeWomenPage':
            return Ga4EventLocation.home;

        case 'ItxProductPage':
            return Ga4EventLocation.fichaProducto;

        default:
            return Ga4EventLocation.pagina;
    }
};

export const getImLocationByEventFrom = (eventFrom: EventFrom, page?: string): Ga4EventLocation => {
    switch (eventFrom) {
        case EventFrom.header:
        case EventFrom.mainMenu:
            return eventFrom as unknown as Ga4EventLocation;

        case EventFrom.productdetail:
            return Ga4EventLocation.fichaProducto;

        case EventFrom.fichaBundle:
            return Ga4EventLocation.fichaBundle;

        case EventFrom.filter:
        case EventFrom.parrilla:
            return Ga4EventLocation.parrilla;

        case EventFrom.chat:
        case EventFrom.smartAssistant:
            return getImLocationByPage(page);

        case EventFrom.miniWishList:
        case EventFrom.wishList:
            return Ga4EventLocation.wishlist;

        default:
            return Ga4EventLocation.pagina;
    }
};

export const getImLocation = (
    eventFrom: EventFrom,
    modalData?: ModalData<any>,
    page?: string
): Ga4EventLocation | undefined => {
    if (isValidModal(modalData)) {
        return Ga4EventLocation.popup;
    }
    return getImLocationByEventFrom(eventFrom, page);
};

export const getDetailForFiltersGroup = (filterEmitter: FilterEmitter): string | undefined => {
    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!filterEmitter.filtersGroupArray) {
        return Object.entries(filterEmitter.filtersGroupArray[filterEmitter.filtersGroup]).reduce((acc, [key, value]) => {
            if (value) {
                acc.push(key);
            }
            return acc;
        }, []).join(',');
    }

    return undefined;
};

export const getDetailForFilter = (filterEmitter: FilterEmitter | orderTypes): string => {
    if (filterEmitter instanceof Object && 'filtersGroup' in filterEmitter) {
        return getDetailForFiltersGroup(filterEmitter);
    }
    return filterEmitter;
};

export const getErrorServerDetail = (errorData: HttpErrorResponse): string => {
    const serviceName = {
        _APP_PERSON_REGISTER: 'sign_up',
    }[errorData?.error?.key];
    const errorName = {
        'DynamicError.logonAlreadyExists': 'user_already_exists',
    }[errorData?.error?.causes?.[0]?.code];

    if (!errorName) return undefined;
    if (!serviceName) return errorName;
    return `${serviceName}:${errorName}`;
};

export const generateImDetailErrorForm = (formValues: FormValueFlatten[], formName: string): string => {
    let paramValue = '';
    for (const formValue of formValues) {
        if (!formValue.valid && !!formValue.errors) {
            for (const error of Object.keys(formValue.errors)) {
                paramValue += `${formName}_${formValue.name}_${error}|`;
            }
        }
    }
    // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
    return paramValue.replace(/\|+$/, '');
};

export const generateImDetailErrorServidorUnidadestock = (items: CartItem[]) => {
    const item: CartItem = items?.[0];
    if (item) {
        const neededUnits = item.quantity - item.unitsAvailable;
        return `${neededUnits}_${item.unitPrice} ${item.reference}`;
    }
    return undefined;
};

export const getDetail = (
    errorType: ErrorType,
    formName: string,
    formValues: FormValueFlatten[],
    errorData: Partial<HttpErrorResponse>,
    filterEmitter: FilterEmitter | orderTypes,
    items: CartItem[],
): string | undefined => {
    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!errorType) {
        switch (errorType) {
            case 'error_cesta':
                return `cod_${errorData?.error?.key}`;

            case 'error_form':
                return generateImDetailErrorForm(formValues, formName);

            case 'error_server_form':
                return `${formName}_cod_${errorData?.error?.key}-desc_${errorData?.error?.description}`;

            case 'error_servidor_unidadestock':
                return generateImDetailErrorServidorUnidadestock(items);

            case 'error_page':
            case 'error_translation':
            case 'error_payment_type':
            case 'error_shipping_tier':
                return errorData?.error?.description;

            default:
                return undefined;
        }
    }
    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!filterEmitter) {
        return getDetailForFilter(filterEmitter);
    }
};

// Lógica incompleta para fase D
export const getActionTypeSelectItem = (eventFrom: EventFrom, searchInfo: GA4SearchInfo): Ga4SelectItemActionType => {
    switch (eventFrom) {
        case EventFrom.search: {
            const origin = searchInfo.origin;
            if (origin !== 'default' && origin !== 'empathize_term') {
                return 'seleccionar_producto_cross';
            }
            break;
        }
        case EventFrom.completeLook:
            return 'seleccionar_producto_cross';
        default:
            return 'seleccionar_producto';
    }
    return 'seleccionar_producto';
};

export const getActionTypeAddToWishlist = (values: GA4EventDataValues) => {
    const item = values?.items[0] as ProductPublicModels.PublicProduct;
    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!item?.bundleProducts?.length) return GA4ImActionType.anadir_look_wishlist;
    if ([EventFrom.completeLook, EventFrom.recentlyViewed, EventFrom.relateds, EventFrom.relatedsBuyed]
        .includes(values.eventFrom)) {
        return GA4ImActionType.anadir_producto_cross_wishlist;
    }
    return GA4ImActionType.anadir_product_wishlist;
};

export const getActionTypeAddToCart = (values: GA4EventDataValues) => {
    const item = values?.items[0] as ProductPublicModels.PublicProduct;
    if (values?.imActionType) return values.imActionType;
    // biome-ignore lint/complexity/noExtraBooleanCast: <explanation>
    if (!!item?.bundleProducts?.length) return GA4ImActionType.anadir_look_cesta;
    if ([EventFrom.completeLook, EventFrom.recentlyViewed, EventFrom.relateds, EventFrom.relatedsBuyed]
        .includes(values.eventFrom)) {
        return GA4ImActionType.anadir_producto_cross_cesta;
    }
    if ([EventFrom.wishList, EventFrom.miniWishList].includes(values?.eventFrom)) {
        return GA4ImActionType.anadir_producto_wishlist_cesta;
    }
    if ([EventFrom.shopcart, EventFrom.miniCart].includes(values?.eventFrom)) {
        return GA4ImActionType.modificar_producto_cesta_ok;
    }
    return GA4ImActionType.anadir_producto_cesta;
};

export const getActionTypeBeginCheckout = () => {
    return 'tramitar_pedido';
};

export const getActionTypeRemoveFromCart = (values: GA4EventDataValues) => {
    if (values?.imActionType) return values.imActionType;
    if ([EventFrom.shopcart, EventFrom.miniCart].includes(values?.eventFrom)) {
        return GA4ImActionType.modificar_producto_cesta_ok;
    }
    return GA4ImActionType.eliminar;
};

export const getActionType = (values: GA4EventDataValues): string => {
    switch (values?.eventType) {
        case GA4EventType.select_item:
            return getActionTypeSelectItem(values.eventFrom, values.searchInfo);

        case GA4EventType.add_to_wishlist:
            return getActionTypeAddToWishlist(values);

        case GA4EventType.add_to_cart:
            return getActionTypeAddToCart(values);

        case GA4EventType.begin_checkout:
            return getActionTypeBeginCheckout();

        case GA4EventType.remove_from_cart:
            return getActionTypeRemoveFromCart(values);

        default:
            return undefined;
    }
};

export const getEventName = (): string => {
    return 'form_submit';
};

export const getImCategoryViewType = (values: GA4EventDataValues): string => {
    switch (values.eventType) {
        case GA4EventType.change_view_item_list:
            return `cat_${gridTypeToNumberOfProducts[values.category_view_type]}`;
        default: {
            // biome-ignore lint/suspicious/useIsArray: <explanation>
            if (values.items instanceof Array) {
                return `cat_${values.items.length}`;
            }
            // biome-ignore lint/suspicious/useIsArray: <explanation>
            if (values.items?.products instanceof Array) {
                return `cat_${values.items.products.length}`;
            }
        }
    }
};

export const getCategoryNavigation = (menuCategories: MenuCategory[], category: Category | MenuCategory): MenuCategory => {
    if (!category || menuCategories?.length <= 0) {
        return undefined;
    }
    return menuCategories.find(menuCategory => menuCategory.id === category.id);
};

export const getLinkUrl = (
    locationOrigin: string,
    menuCategories: MenuCategory[],
    category: Category | MenuCategory
): string => {
    const locationPath = getCategoryNavigation(menuCategories, category)?.locationPath;

    if (!locationPath) {
        return undefined;
    }

    return locationOrigin + locationPath;
};

export const getLinkText = (
    locationOrigin: string,
    menuCategories: MenuCategory[],
    category: Category | MenuCategory
): string => {
    if (!locationOrigin && menuCategories?.length && category) {
        const menuCategory = getCategoryNavigation(menuCategories, category);
        return menuCategory?.name;
    }

    return locationOrigin;
};

export const getImProductColor = (item: ProductPublicModels.PublicProduct | CartItem): string => {
    if (Ga4DataUtils.isProduct(item)) {
        return (item as ProductPublicModels.PublicProduct)?.status?.selectedColor?.id;
    }
    return (item as CartItem)?.colorId;
};

export const getImProductSize = (item: ProductPublicModels.PublicProduct | CartItem): string => {
    let sizeId: string;

    if (Ga4DataUtils.isProduct(item)) {
        sizeId = (item as ProductPublicModels.PublicProduct)?.status?.selectedSize?.mastersSizeId?.toString();
    } else {
        sizeId = (item as CartItem)?.selectedSize?.mastersSizeId?.toString();
    }

    return sizeId?.length > 2
        ? sizeId.substring(1)
        : sizeId;
};

export const getImBundleReference = (
    imBundleReference: string,
    item: ProductPublicModels.PublicProduct | CartItem
): string => {
    return imBundleReference || (item as any)?.bundleReference;
};

export const getImCategoryCrossType = (relatedProductType?: RelatedProductType): EventFrom | undefined => {
    const relatedProductDicc: Record<RelatedProductType, EventFrom> = {
        cart_yoda: EventFrom.cartYoda,
        completatulook_yoda: EventFrom.completeLookYoda,
        completeLook: EventFrom.completeLook,
        floren: EventFrom.relateds,
        frecuently_bought_together: EventFrom.frecuentlyBoughtTogether,
        other_you_may_like: EventFrom.othersYouMayLike,
        recentlyViewed: EventFrom.recentlyViewed,
        recommended_for_you: EventFrom.relateds,
        similar: EventFrom.similar,
        similares_yoda: EventFrom.similaresYoda,
        top_sales: EventFrom.topSales,
        wishlist: EventFrom.wishList,
    };

    return relatedProductDicc[relatedProductType];
};

export const getSearchTerm = (eventFrom: EventFrom, searchInfo: GA4SearchInfo): string => {
    return eventFrom === EventFrom.searchAdapter ? searchInfo.searchTerm : searchInfo.search_param;
};

export const getImItemId = (item: CartItem | ProductPublicModels.PublicProduct) => {
    return (item as CartItem)?.parentReference || (item as ProductPublicModels.PublicProduct)?.reference;
};

// No necesario para la action type actualmente implementada
export const getImStoreZipCode = () => {
    return undefined;
};

export const getParamValue = (param: GA4EventDataParam, values: GA4EventDataValues): string | undefined => {
    const item: CartItem | ProductPublicModels.PublicProduct = values?.items?.[0];
    switch (param) {
        case GA4EventDataParam.content_id:
            return values.contentId;

        case GA4EventDataParam.content_type:
            return values.contentType;

        case GA4EventDataParam.content_value:
            return values.contentValue;

        case GA4EventDataParam.im_app_store:
            return values.imAppStore;

        case GA4EventDataParam.im_items_number:
            return getItemsNumber(values);

        case GA4EventDataParam.im_item_id:
            return getImItemId(item);

        case GA4EventDataParam.im_type:
            return values.imType ?? getImType(values);

        case GA4EventDataParam.im_carousel_index:
            return values?.imCarouselIndex;

        case GA4EventDataParam.im_event_data_cf:
            return values?.imEventDataCf || getImEventDataCF(values?.eventFrom, values?.modal, values?.checkoutAuth);

        case GA4EventDataParam.search_term:
            return getSearchTerm(values.eventFrom, values.searchInfo);

        case GA4EventDataParam.im_location:
            return values.imLocation || getImLocation(values.eventFrom, values.modal, values.page);

        case GA4EventDataParam.method:
            return values?.method;

        case GA4EventDataParam.item_id:
        case GA4EventDataParam.im_bundle_reference:
            return getImBundleReference(values?.imBundleReference, item);

        case GA4EventDataParam.im_physical_store_id:
            return undefined;

        case GA4EventDataParam.im_collection_reference:
            return getCollectionReference(values?.category);

        case GA4EventDataParam.im_photo_reference:
            return getPhotoReference(item as ProductPublicModels.PublicProduct, values?.mediaPath);

        case GA4EventDataParam.im_event_name:
            return getEventName();

        case GA4EventDataParam.im_detail:
            return values?.imDetail || getDetail(
                values.errorType,
                values.formName,
                values.formValues,
                values.errorData,
                values.filterEmitter,
                values.items as CartItem[],
            );

        case GA4EventDataParam.im_action_type:
            return values?.imActionType || getActionType(values);

        case GA4EventDataParam.im_link_category:
            return getCategoryNavigation(values.categoriesNavigation, values.category)?.sectionPathEn;

        case GA4EventDataParam.im_link_page_title: {
            return getFullPageTitle({
                navigationData: { modal: values.modal, page: values.page, routerState: values.router },
                product: values.product,
                lastSection: values.lastSection,
                shopCartData: values.shopCartData,
                categoryInfo: {
                    category: values.category,
                    categoriesNavigation: values.categoriesNavigation,
                    lookbookCategory: values.lookbookCategory,
                },
            } as CustomContext, values.eventFrom);
        }

        case GA4EventDataParam.im_link_type:
            return values?.imLinkType || 'texto';

        case GA4EventDataParam.im_product_color:
            return getImProductColor(item);

        case GA4EventDataParam.im_product_size:
            return getImProductSize(item);

        case GA4EventDataParam.link_text:
            return (
                values?.linkText || getLinkText(values.linkText, values.categoriesNavigation, values.category)
            )?.toLowerCase();

        case GA4EventDataParam.im_order_state:
            return values?.imOrderState;

        case GA4EventDataParam.im_shipping_tier:
            return GA4EcommerceHelpers.getShippingType(values.eventType, values.eventFrom, values.shippingMethod, undefined);

        case GA4EventDataParam.im_transaction_id:
            return values?.imTransactionId;

        case GA4EventDataParam.link_url:
            return values?.linkUrl || getLinkUrl(values.locationOrigin, values.categoriesNavigation, values.category);

        case GA4EventDataParam.im_photo_type:
            return values?.imPhotoType?.toString();

        case GA4EventDataParam.im_category_cross_type:
            return getImCategoryCrossType(values.relatedProductType);

        case GA4EventDataParam.im_category_view_type:
            return getImCategoryViewType(values);

        case GA4EventDataParam.size_recommender_name:
            return 'sastre';

        case GA4EventDataParam.im_store_zip_code:
            return getImStoreZipCode();

        default:
            return undefined;
    }
};

const getParams = (config: GA4EventDataParam[], values: GA4EventDataValues): Record<GA4EventDataParam, any> => {
    const paramValue: Record<GA4EventDataParam, any> = { ...defaultConfig };
    for (const param of config) {
        paramValue[param] = getParamValue(param, values);
    }

    return paramValue;
};

export function ga4EventDataProcessConfig(
    config: GA4EventDataParam[],
    eventData: GA4EventDataValues,
): GA4EventDataGroup | undefined {
    if (config?.length) {
        const newConfig: GA4EventDataGroup = { ...defaultConfig, ...getParams(config, eventData) };
        return newConfig;
    }
    return undefined;
}
